/**
 * Pre-auth auth-provider lookup (Phase 95 Plan 95-02, Task 1, D-03).
 *
 * IMPORTANT: this module DOES NOT route through services/api/client.ts.
 * Doing so would clear localStorage.monksheets_auth_token on any 401 (see
 * client.ts:28) and would fire the global `auth:expired` navigation — both
 * of which are internal-session semantics that must never trigger from an
 * unauthenticated, pre-login call. This follows the same rationale as
 * services/api/externalAuth.ts: use raw `fetch` directly so no internal
 * auth token state is ever touched by this module.
 *
 * `GET {VITE_API_URL}/auth/config` is public (no auth, no cookie required)
 * and is built in Plan 95-05. Until that backend endpoint exists (or if it
 * ever becomes unreachable), `fetchAuthConfig` resolves `DEFAULT_AUTH_CONFIG`
 * so the login page keeps working via the pre-existing Google route
 * (deploy-skew / network-failure fallback, D-03 / T-95-08).
 */

export type AuthProvider = 'google' | 'oidc';

export interface AuthConfig {
  provider: AuthProvider;
  loginPath: string;
}

/**
 * Deploy-skew fallback: a frontend deployed ahead of the backend that
 * exposes `GET /api/auth/config` (or a backend that is temporarily
 * unreachable) must keep working on the pre-existing Google route.
 */
export const DEFAULT_AUTH_CONFIG: AuthConfig = { provider: 'google', loginPath: '/auth/google' };

interface AuthConfigPayload {
  data?: {
    provider?: unknown;
    loginPath?: unknown;
  };
}

function isValidAuthConfig(body: unknown): body is { data: AuthConfig } {
  if (typeof body !== 'object' || body === null) return false;
  const data = (body as AuthConfigPayload).data;
  if (typeof data !== 'object' || data === null) return false;
  const { provider, loginPath } = data as { provider?: unknown; loginPath?: unknown };
  if (provider !== 'google' && provider !== 'oidc') return false;
  // WR-04 (Phase 95 code review): a bare `startsWith('/')` check admits
  // protocol-relative values like `//evil.example/phish` (they DO start
  // with a single `/`). Reject a second leading `/` too, so `loginPath`
  // can only ever be a same-origin relative path, never something the
  // browser could reinterpret as pointing at a different authority.
  if (typeof loginPath !== 'string' || !loginPath.startsWith('/') || loginPath.startsWith('//')) {
    return false;
  }
  return true;
}

/**
 * Resolves the backend-reported auth provider config. Never rejects and
 * never throws — any failure (network error, 404, malformed body, invalid
 * provider/loginPath) resolves `DEFAULT_AUTH_CONFIG` instead (T-95-08).
 */
export async function fetchAuthConfig(): Promise<AuthConfig> {
  const base = import.meta.env.VITE_API_URL || '/api';
  try {
    const response = await fetch(`${base}/auth/config`, { credentials: 'include' });
    if (!response.ok) return DEFAULT_AUTH_CONFIG;
    const body = await response.json();
    if (!isValidAuthConfig(body)) return DEFAULT_AUTH_CONFIG;
    return { provider: body.data.provider, loginPath: body.data.loginPath };
  } catch {
    return DEFAULT_AUTH_CONFIG;
  }
}
